import { Link } from "react-router-dom";
import { Calendar, AlertTriangle, MessageSquare, Wallet, History } from "lucide-react";
import StatusBadge from "./StatusBadge";
import TestDataBadge from "../common/TestDataBadge";
import DecidedBy from "../common/DecidedBy";
import DecisionNotes from "../common/DecisionNotes";
import WaitingOn from "../common/WaitingOn";
import { LEAVE_TYPE_LABEL } from "../../lib/leaveOptions";
import { formatISTDate } from "../../lib/datetime";
import { joinParts, plural } from "../../lib/vocabulary";

/**
 * `balance` is the filer's entitlement summary. It is shown only on a pending
 * leave, because that is the moment it changes a decision — "2 days, 9 left" is
 * an easy yes and "2 days, 1 left" is not. On a decided leave it is history and
 * would misrepresent the balance at the time the call was made.
 */
export default function LeaveCard({ leave, showOfficer = false, action = null, balance = null }) {
  const forType = balance?.[leave.leave_type];
  const showBalance = forType && leave.status === "pending";
  // Dates in IST prose, never the raw ISO the API stores, and the half-day
  // separator only exists when there is a half day to separate (joinParts —
  // the trailing " · " findings were all a separator baked into a template).
  const dateLine =
    leave.from_date === leave.to_date
      ? joinParts([
          formatISTDate(leave.from_date),
          leave.half_day
            ? leave.half_day === "first_half"
              ? "1st half"
              : "2nd half"
            : null,
        ])
      : `${formatISTDate(leave.from_date)} → ${formatISTDate(leave.to_date)}`;

  return (
    <div
      className="bg-white border border-callisto rounded-xl p-4"
      data-testid={`leave-card-${leave.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <Link to={`/leaves/${leave.id}`} className="min-w-0 flex-1 group">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-bold text-callisto-navy leading-tight truncate group-hover:underline">
              {LEAVE_TYPE_LABEL[leave.leave_type]} Leave
            </h3>
            <span className="text-[11px] font-semibold rounded-full bg-callisto-navy/5 text-callisto-navy px-2 py-0.5">
              {plural(leave.days_count, "day")}
            </span>
            {leave.is_lop && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold rounded-full bg-orange-50 text-orange-700 border border-orange-200 px-1.5 py-0.5">
                <AlertTriangle size={10} />
                LOP {leave.lop_days}
              </span>
            )}
            {/* P6: past-dated filing is permitted and SAID, so the approver
                reads the fact instead of comparing dates. */}
            {leave.retrospective === true && (
              <span
                className="inline-flex items-center gap-0.5 text-[10px] font-bold rounded-full bg-sky-50 text-sky-800 border border-sky-200 px-1.5 py-0.5"
                data-testid={`leave-retrospective-${leave.id}`}
              >
                <History size={10} />
                Retrospective
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-callisto-muted flex items-center gap-1.5">
            <Calendar size={12} />
            {dateLine}
          </p>
          {showBalance && (
            <p
              className="mt-1 text-[11px] flex items-center gap-1"
              data-testid={`leave-balance-${leave.id}`}
            >
              <Wallet size={11} className="text-callisto-navy/60" />
              <span className="text-callisto-muted">
                {LEAVE_TYPE_LABEL[leave.leave_type]} balance:
              </span>
              <span
                className={`font-bold ${
                  forType.balance < leave.days_count
                    ? "text-orange-700"
                    : "text-callisto-navy"
                }`}
              >
                {forType.balance} of {forType.accrued} left
              </span>
              {forType.balance < leave.days_count && (
                <span className="text-orange-700">· would go to LOP</span>
              )}
            </p>
          )}
          {showOfficer && leave.sales_officer_name && (
            <p className="mt-0.5 text-[11px] text-callisto-muted truncate">
              by{" "}
              <span className="font-semibold text-callisto-navy">
                {leave.sales_officer_name}
              </span>
            </p>
          )}
          {leave.reason && (
            <p className="mt-2 text-xs text-callisto-body line-clamp-2 flex items-start gap-1.5">
              <MessageSquare size={12} className="shrink-0 mt-[2px] text-callisto-navy/60" />
              <span>{leave.reason}</span>
            </p>
          )}
        </Link>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className="flex flex-col items-end gap-1">
            <StatusBadge status={leave.status} />
            <TestDataBadge record={leave} className="ml-1" />
            <DecidedBy record={leave} className="text-right" />
          </div>
          {action}
        </div>
      </div>
      {/* Same two lines the expense card carries: where an in-flight claim
          sits, and why a decided one came back. A pending leave used to say
          nothing at all between filing and decision. */}
      <WaitingOn record={leave} className="mt-2.5" />
      <DecisionNotes record={leave} className="mt-2" />
    </div>
  );
}
